
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth";
import { fetchServiceProviderAdmin } from "@/services/providers/providerAdminReadService";
import { getAllCategoriesWithId } from "@/services/categoryService";
import { getAllAreas } from "@/services/areaService";
import { getAllUserProfiles } from "@/services/profileService";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminEditListingPreview from "@/components/admin/edit-listing/AdminEditListingPreview";
import AdminEditListingForm from "@/components/admin/edit-listing/AdminEditListingForm";
import AdminEditListingActions from "@/components/admin/edit-listing/AdminEditListingActions";
import { ServiceProviderRow } from "@/types/supabase";
import { useToast } from "@/hooks/use-toast";
import { useApproveAdminListing, useRejectAdminListing, useUpdateAdminListing } from "@/hooks/admin/useAdminListingActions";
import RejectReasonModal from "@/components/admin/RejectReasonModal";

interface FormData {
  name: string;
  categories: string[];
  description: string;
  websiteUrl: string;
  suburbs: string[];
  imageUrl: string;
  isFeatured: boolean;
  isVerified: boolean;
  // Contact fields for unassigned listings
  whatsappNumber: string;
  // User assignment
  assignedUserId: string | null;
}

const AdminEditListingPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const updateListing = useUpdateAdminListing();
  const approveListing = useApproveAdminListing();
  const rejectListing = useRejectAdminListing();

  const [formData, setFormData] = useState<FormData>({
    name: "",
    categories: [],
    description: "",
    websiteUrl: "",
    suburbs: [],
    imageUrl: "",
    isFeatured: false,
    isVerified: false,
    whatsappNumber: "",
    assignedUserId: null,
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);

  const {
    data: listing,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin-edit-listing", id],
    queryFn: async () => {
      if (!id) throw new Error("Listing ID is required");

      return await fetchServiceProviderAdmin(id);
    },
    enabled: !!id,
  });

  // Use useEffect instead of deprecated onSuccess
  useEffect(() => {
    if (listing) {
      setFormData({
        name: listing.name || "",
        categories: listing.categories || [],
        description: listing.description || "",
        websiteUrl: listing.website_url || "",
        suburbs: listing.suburbs || [],
        imageUrl: listing.image_url || "",
        isFeatured: listing.is_featured || false,
        isVerified: listing.is_verified || false,
        whatsappNumber: listing.whatsapp_number || "",
        assignedUserId: listing.user_id || null,
      });
    }
  }, [listing]);

  const {
    data: categories = [],
  } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      return await getAllCategoriesWithId();
    },
  });

  const {
    data: areas = [],
  } = useQuery({
    queryKey: ["areas"],
    queryFn: async () => {
      return await getAllAreas();
    },
  });

  const {
    data: users = [],
  } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      return await getAllUserProfiles();
    },
  });

  const areasGrouped = areas.reduce((acc, area) => {
    if (!acc[area.territory]) {
      acc[area.territory] = [];
    }
    acc[area.territory].push(area.name);
    return acc;
  }, {} as Record<string, string[]>);

  useEffect(() => {
    if (updateListing.isSuccess) {
      toast({
        title: "Listing Updated",
        description: "The service listing has been successfully updated.",
      });
    }
  }, [updateListing.isSuccess, toast]);

  useEffect(() => {
    if (updateListing.isError) {
      const message = updateListing.error instanceof Error ? updateListing.error.message : 'Failed to update the service listing.';
      toast({
        title: "Update Failed",
        description: message,
        variant: "destructive",
      });
    }
  }, [updateListing.isError, updateListing.error, toast]);

  useEffect(() => {
    if (approveListing.isSuccess) {
      toast({
        title: "Listing Approved",
        description: "The service listing has been approved.",
      });
    }
  }, [approveListing.isSuccess, toast]);

  useEffect(() => {
    if (approveListing.isError) {
      const message = approveListing.error instanceof Error ? approveListing.error.message : 'Failed to approve the service listing.';
      toast({
        title: "Approval Failed",
        description: message,
        variant: "destructive",
      });
    }
  }, [approveListing.isError, approveListing.error, toast]);

  useEffect(() => {
    if (rejectListing.isSuccess) {
      toast({
        title: "Listing Rejected",
        description: "The service listing has been rejected.",
      });
      setRejectModalOpen(false);
    }
  }, [rejectListing.isSuccess, toast]);

  useEffect(() => {
    if (rejectListing.isError) {
      const message = rejectListing.error instanceof Error ? rejectListing.error.message : 'Failed to reject the service listing.';
      toast({
        title: "Rejection Failed",
        description: message,
        variant: "destructive",
      });
    }
  }, [rejectListing.isError, rejectListing.error, toast]);

  const handleImageChange = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFieldChange = (field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleToggle = (field: string) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field as keyof FormData] }));
  };

  const handleCategorySelect = (categoryName: string) => {
    if (!formData.categories.includes(categoryName)) {
      setFormData(prev => ({
        ...prev,
        categories: [...prev.categories, categoryName]
      }));
    }
  };

  const handleCategoryRemove = (category: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== category)
    }));
  };

  const handleSuburbAdd = (suburb: string) => {
    if (!formData.suburbs.includes(suburb)) {
      setFormData(prev => ({
        ...prev,
        suburbs: [...prev.suburbs, suburb]
      }));
    }
  };

  const handleSuburbRemove = (suburb: string) => {
    setFormData(prev => ({
      ...prev,
      suburbs: prev.suburbs.filter(s => s !== suburb)
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!listing || !user) return;
    
    const updates: Partial<ServiceProviderRow> = {
      name: formData.name,
      category: formData.categories[0] || listing.category,
      categories: formData.categories,
      description: formData.description,
      website_url: formData.websiteUrl,
      suburbs: formData.suburbs,
      image_url: imagePreview || formData.imageUrl || "/default-banner.png",
      is_featured: formData.isFeatured,
      is_verified: formData.isVerified,
    };

    // Handle user assignment
    if (formData.assignedUserId !== listing.user_id) {
      updates.user_id = formData.assignedUserId || undefined;
    }

    // Include contact fields for unassigned listings
    if (!formData.assignedUserId) {
      updates.whatsapp_number = formData.whatsappNumber || undefined;
    }
    
    updateListing.mutate({
      listingId: listing.id!,
      name: updates.name,
      description: updates.description,
      category: updates.category,
      categories: updates.categories,
      suburbs: updates.suburbs,
      websiteUrl: updates.website_url,
      imageUrl: updates.image_url,
      isFeatured: updates.is_featured,
      isVerified: updates.is_verified,
      ownerUserId: updates.user_id,
      whatsappNumber: updates.whatsapp_number,
    });
  };

  const handleRejectConfirm = async (reason: string) => {
    if (!listing?.id) {
      return;
    }

    rejectListing.mutate({ listingId: listing.id, reason });
  };


  if (isLoading) {
    return <AdminLayout title="Edit Listing" loading={true} />;
  }

  if (isError) {
    return (
      <AdminLayout title="Edit Listing">
        <div className="text-red-500">Error: {(error as Error)?.message}</div>
      </AdminLayout>
    );
  }

  if (!listing) {
    return (
      <AdminLayout title="Edit Listing">
        <div className="text-red-500">Listing not found.</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      title="Edit Listing" 
      listingName={listing?.name}
      listingStatus={listing?.status}
      onBack={() => navigate("/admin/review-listings")}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <AdminEditListingForm
            formData={formData}
            categories={categories}
            areasGrouped={areasGrouped}
            users={users}
            isDisabled={updateListing.isPending}
            isUnassigned={!listing.user_id}
            onSubmit={handleSubmit}
            onFieldChange={handleFieldChange}
            onToggle={handleToggle}
            onCategorySelect={handleCategorySelect}
            onCategoryRemove={handleCategoryRemove}
            onSuburbAdd={handleSuburbAdd}
            onSuburbRemove={handleSuburbRemove}
          />
        </div>
        <div>
          <AdminEditListingPreview
            formData={formData}
            imagePreview={imagePreview}
            onImageChange={handleImageChange}
          />
        </div>
      </div>
      <AdminEditListingActions
        listing={listing}
        isUpdatePending={updateListing.isPending}
        isStatusPending={approveListing.isPending || rejectListing.isPending}
        onSave={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
        onApprove={() => approveListing.mutate({ listingId: listing.id! })}
        onReject={() => setRejectModalOpen(true)}
      />
      <RejectReasonModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={handleRejectConfirm}
        providerName={listing.name || ""}
      />
    </AdminLayout>
  );
};

export default AdminEditListingPage;
